import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CONTENTTYPE_FILE,
  CONTENTTYPE_TEXT,
  HEARINGSTATUS_AWAITING_CONCLUSION,
  HEARINGSTATUS_CONCLUDED,
} from '../../../../../constants/api';
import { currentUserOwnsHearingByIdSelector } from '../../../../../features/apiFeatures/hearingSlice';
import {
  isUpdatingHearingSelector,
  updateFieldDataInitialize,
} from '../../../../../features/componentFeatures/renderHearingSlice';
import { getAttr } from '../../../../../features/helpers/apiHelpers';
import { hearingStatusByHearingIdSelector } from '../../../../../features/selectors/compositeSelectors';
import {
  mapFileContentToFileItems,
  optionsMapperName,
} from '../../../../../utilities/mapperFunctions';
import { ConclusionFieldRead } from './conclusionField/ConclusionFieldRead';
import { ConclusionFieldWrite } from './conclusionField/ConclusionFieldWrite';
import { downloadFileFromUrl } from '../../../../../utilities/files';
import {
  isUpdatingContentSelector,
  setIsNotificationPanelOpen,
} from '../../../../../features/componentFeatures/notificationPanelSlice';

const ConclusionField = ({ fieldData, onChange, validationEntity }) => {
  const dispatch = useDispatch();

  const [conclusionTextContent] = fieldData.textContent || [];
  const conclusionFiles = fieldData.fileContent || [];
  const fieldTemplates = fieldData.templates || [];
  const templateOptions = fieldTemplates.map(optionsMapperName);
  const conclusionTextValue = getAttr(conclusionTextContent, 'textContent');
  const files = conclusionFiles.map(mapFileContentToFileItems);
  const isUpdatingHearing = useSelector(isUpdatingHearingSelector);
  const isUpdatingNotificationContent = useSelector(isUpdatingContentSelector);

  const onChangeText = (value) => onChange(value, CONTENTTYPE_TEXT);
  const onChangeFiles = (value, isDeleteOperation) =>
    onChange(value, CONTENTTYPE_FILE, true, isDeleteOperation);

  const onDownloadFile = (value) =>
    value && downloadFileFromUrl(value.urlToDownload, value.fileName);

  const hearingStatus = useSelector(
    hearingStatusByHearingIdSelector(fieldData.hearingId),
  );
  const currentStatus = getAttr(hearingStatus, 'status');

  const isCurrentUserOwnerOfHearing = useSelector(
    currentUserOwnsHearingByIdSelector(fieldData.hearingId),
  );

  const isReadMode = currentStatus === HEARINGSTATUS_CONCLUDED;
  const isWriteMode =
    currentStatus === HEARINGSTATUS_AWAITING_CONCLUSION &&
    isCurrentUserOwnerOfHearing;

  const onSaveConclusion = useCallback(
    () =>
      dispatch(
        updateFieldDataInitialize({
          hearingId: fieldData.hearingId,
          newStatus: currentStatus,
        }),
      ),
    [dispatch, fieldData, currentStatus],
  );

  const onSaveAndFinalize = useCallback(
    () =>
      dispatch(
        updateFieldDataInitialize({
          hearingId: fieldData.hearingId,
          newStatus: HEARINGSTATUS_CONCLUDED,
        }),
      ),
    [dispatch, fieldData],
  );

  const onEditNotification = () => dispatch(setIsNotificationPanelOpen(true));

  if (isReadMode) {
    return (
      <ConclusionFieldRead
        conclusionTextValue={conclusionTextValue}
        onDownloadFile={onDownloadFile}
        files={files}
      />
    );
  } else if (isWriteMode) {
    return (
      <ConclusionFieldWrite
        hearingId={fieldData.hearingId}
        conclusionTextValue={conclusionTextValue}
        onChange={onChangeText}
        templateOptions={templateOptions}
        onSaveConclusion={onSaveConclusion}
        onSaveAndFinalize={onSaveAndFinalize}
        onEditNotification={onEditNotification}
        validationEntity={validationEntity}
        onChangeFiles={onChangeFiles}
        onDownloadFile={onDownloadFile}
        files={files}
        showSpinner={isUpdatingHearing || isUpdatingNotificationContent}
      />
    );
  } else {
    return null;
  }
};

export { ConclusionField };
